import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

/* ============================================================
 * Shared UI primitives — "Copper & Silicon" design system
 * ============================================================ */

export const Container = styled.div`
  max-width: var(--container);
  margin: 0 auto;
  padding: 0 clamp(1.25rem, 4vw, 2.5rem);
`;

export const Section = styled.section`
  padding: var(--section-pad) 0;
  position: relative;
  ${(p) =>
    p.$bordered &&
    css`
      border-top: 1px solid var(--line);
    `}
  ${(p) =>
    p.$elevated &&
    css`
      background: var(--bg-elev);
    `}
`;

/* --- Mono instrument label, e.g. [ OVERVIEW / 01 ] ----------- */
const KickerWrap = styled.p`
  font-family: var(--font-mono);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--copper);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.4rem;

  &::before {
    content: '';
    width: 2.25rem;
    height: 1px;
    background: var(--copper);
    flex: none;
  }
`;

export function Kicker({ index, children, ...rest }) {
  return (
    <KickerWrap {...rest}>
      {children}
      {index && <span style={{ color: 'var(--ink-faint)' }}>/ {index}</span>}
    </KickerWrap>
  );
}

export const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4.5vw, 3.25rem);
  font-weight: 800;
  max-width: 18ch;
  margin-bottom: 1.25rem;

  em {
    font-style: normal;
    background: linear-gradient(115deg, var(--copper-bright) 10%, var(--copper) 55%, var(--copper-deep) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

export const Lead = styled.p`
  color: var(--ink-dim);
  font-size: clamp(1rem, 1.4vw, 1.15rem);
  line-height: 1.75;
  max-width: 62ch;
`;

/* --- Buttons: HUD tech-frame ----------------------------------
 * Anatomy (matches the hero mockup):
 *   · chamfered body — big cut TL/BR, small cut TR/BL (CSS clip-path, px-true)
 *   · floating outer frame — an SVG child offset outside the body, so it
 *     survives clipping; vector-effect keeps strokes crisp at any size
 *   · copper end-cap accents (primary) or corner ticks (ghost)
 *   · dash detail at the body's bottom-left (primary)
 * ---------------------------------------------------------------- */

function HudFrame() {
  return (
    <svg className="hud-frame" viewBox="0 0 320 88" preserveAspectRatio="none" aria-hidden="true">
      <polygon className="rail rail-outer" points="23,3 291,3 317,29 317,61 291,85 22,85 3,66 3,22" />
      <polygon className="rail rail-mid" points="28,9 286,9 310,32 310,58 285,79 27,79 10,62 10,27" />
      <polygon className="rail rail-inner" points="33,14 282,14 303,34 303,55 281,73 32,73 16,58 16,30" />
      <polyline className="accent accent-tl" points="23,3 64,3 70,7" />
      <polyline className="accent accent-tr" points="252,9 286,9 310,32" />
      <polyline className="accent accent-bl" points="3,66 22,85 116,85" />
      <polyline className="accent accent-br" points="218,79 285,79 310,58" />
      <polyline className="accent side-rail" points="10,27 10,62 18,70" />
      <polyline className="accent side-rail" points="302,24 310,32 310,58 300,67" />
      <g className="micro-bars">
        <line x1="101" y1="9" x2="113" y2="9" />
        <line x1="118" y1="9" x2="125" y2="9" />
        <line x1="193" y1="14" x2="215" y2="14" />
        <line x1="221" y1="14" x2="228" y2="14" />
        <line x1="126" y1="79" x2="164" y2="79" />
        <line x1="169" y1="79" x2="176" y2="79" />
      </g>
      <g className="primary-bars">
        <line x1="132" y1="69" x2="139" y2="78" />
        <line x1="143" y1="69" x2="150" y2="78" />
        <line x1="154" y1="69" x2="161" y2="78" />
        <line x1="165" y1="69" x2="172" y2="78" />
      </g>
    </svg>
  );
}

function SquareHudFrame() {
  return (
    <svg className="hud-frame square-frame" viewBox="0 0 72 72" aria-hidden="true">
      <polygon className="rail rail-outer" points="15,3 57,3 69,15 69,57 57,69 15,69 3,57 3,15" />
      <polygon className="rail rail-mid" points="18,8 54,8 64,18 64,54 54,64 18,64 8,54 8,18" />
      <polygon className="rail rail-inner" points="20,12 52,12 60,20 60,52 52,60 20,60 12,52 12,20" />
      <polyline className="accent accent-tl" points="15,3 30,3" />
      <polyline className="accent accent-br" points="42,69 57,69 69,57" />
    </svg>
  );
}

const hudButtonBase = css`
  --hud-cut: 17px;
  --hud-body-inset: 8px;
  --hud-frame: rgba(120, 128, 137, 0.6);
  --hud-frame-soft: rgba(150, 157, 165, 0.7);
  --hud-frame-inner: rgba(120, 128, 136, 0.3);
  --hud-accent: #d9851f;
  --hud-primary-bars: 0;
  --hud-side-rails: 0;
  --hud-cursor: pointer;
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 66px;
  padding: 1.05rem 2.5rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--ink);
  font-family: var(--font-mono);
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.09em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: var(--hud-cursor);
  filter: drop-shadow(0 8px 14px rgba(48, 42, 30, 0.18));
  transition: transform 0.22s var(--ease-out), filter 0.22s var(--ease-out), color 0.22s var(--ease-out);

  &::before,
  &::after {
    content: '';
    position: absolute;
    pointer-events: none;
    clip-path: polygon(
      var(--hud-cut) 0,
      calc(100% - var(--hud-cut)) 0,
      100% var(--hud-cut),
      100% calc(100% - var(--hud-cut)),
      calc(100% - var(--hud-cut)) 100%,
      var(--hud-cut) 100%,
      0 calc(100% - var(--hud-cut)),
      0 var(--hud-cut)
    );
  }

  &::before {
    inset: var(--hud-body-inset);
    z-index: -2;
    background: var(--hud-rim);
  }

  &::after {
    inset: calc(var(--hud-body-inset) + 2px);
    z-index: -1;
    background: var(--hud-surface);
    box-shadow: var(--hud-inset, inset 0 0 22px rgba(70, 58, 36, 0.1));
  }

  .hud-content {
    position: relative;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
  }

  .hud-content > svg {
    flex: none;
    font-size: 1.12rem;
    transition: transform 0.22s var(--ease-out);
  }

  .hud-frame {
    position: absolute;
    inset: 0;
    z-index: 2;
    width: 100%;
    height: 100%;
    overflow: visible;
    pointer-events: none;

    polygon,
    polyline,
    line {
      fill: none;
      vector-effect: non-scaling-stroke;
      stroke-linecap: square;
      stroke-linejoin: miter;
      transition: stroke 0.22s var(--ease-out), opacity 0.22s var(--ease-out);
    }

    .rail-outer { stroke: var(--hud-frame); stroke-width: 1.15; }
    .rail-mid { stroke: var(--hud-frame-soft); stroke-width: 1; }
    .rail-inner { stroke: var(--hud-frame-inner); stroke-width: 0.9; }
    .accent { stroke: var(--hud-accent); stroke-width: 2.4; }
    .side-rail { opacity: var(--hud-side-rails); stroke-width: 4; }
    .micro-bars line { stroke: var(--hud-accent); stroke-width: 1.4; opacity: 0.72; }
    .primary-bars { opacity: var(--hud-primary-bars); }
    .primary-bars line { stroke: rgba(255, 211, 131, 0.78); stroke-width: 3; }
  }

  &:hover { transform: translateY(-2px); }
  &:hover .hud-content > svg:last-child { transform: translateX(4px); }
  &:active { transform: translateY(0) scale(0.985); }

  ${(p) => p.$size === 'hero' && css`
    --hud-cut: 20px;
    --hud-body-inset: 9px;
    min-height: 84px;
    padding: 1.3rem 3rem;
    font-size: 1.05rem;
    .hud-content { gap: 1.1rem; }
    .hud-content > svg { font-size: 1.55rem; }
  `}

  ${(p) => p.$size === 'nav' && css`
    --hud-cut: 13px;
    --hud-body-inset: 7px;
    min-height: 58px;
    min-width: 188px;
    padding: 0.9rem 1.85rem;
    font-size: 0.78rem;
    letter-spacing: 0.1em;
  `}

  ${(p) => p.$size === 'status' && css`
    --hud-cut: 12px;
    --hud-body-inset: 6px;
    min-height: 54px;
    padding: 0.8rem 1.65rem;
    font-size: 0.76rem;
    letter-spacing: 0.14em;
  `}

  ${(p) => p.$disabled && css`
    cursor: not-allowed;
    pointer-events: none;
  `}

  @media (max-width: 560px) {
    /* keep long labels inside small viewports — nowrap can't fit otherwise */
    max-width: 100%;
    font-size: 0.7rem;
    letter-spacing: 0.05em;
    padding-left: 1.2rem;
    padding-right: 1.2rem;

    .hud-content {
      gap: 0.55rem;
    }

    .hud-content > svg {
      font-size: 0.95rem;
    }

    .hide-sm {
      display: none;
    }

    ${(p) => p.$size === 'hero' && css`
      width: min(100%, 360px);
      min-height: 66px;
      padding: 1rem 1.5rem;
      font-size: 0.82rem;
    `}
  }
`;

const PrimaryBase = styled.a`
  ${hudButtonBase}
  --hud-rim: linear-gradient(110deg, #ffcb77, #8a420b 20%, #ff9d2f 50%, #713309 80%, #ffc369);
  --hud-surface: repeating-linear-gradient(135deg, rgba(255, 220, 160, 0.04) 0 1px, transparent 1px 4px),
    radial-gradient(100% 140% at 27% 0%, rgba(255, 218, 147, 0.42), transparent 45%),
    linear-gradient(180deg, #d87b1d 0%, #b95d0e 100%);
  --hud-frame: #d67b1d;
  --hud-frame-soft: rgba(255, 178, 70, 0.76);
  --hud-frame-inner: rgba(255, 218, 151, 0.45);
  --hud-accent: #ffad39;
  --hud-primary-bars: 1;
  --hud-side-rails: 1;
  --hud-inset: inset 0 0 24px rgba(80, 30, 0, 0.34);
  color: #fffaf2;
  text-shadow: 0 1px 1px rgba(56, 22, 0, 0.88), 0 0 10px rgba(255, 228, 187, 0.18);
  filter: drop-shadow(0 12px 20px rgba(139, 61, 5, 0.34)) drop-shadow(0 0 13px rgba(255, 151, 30, 0.3));

  ${(p) => p.$size === 'hero' && css`min-width: 352px;`}
  ${(p) => p.$disabled && css`
    filter: saturate(0.9) brightness(0.92) drop-shadow(0 8px 14px rgba(139, 61, 5, 0.38));
  `}

  &:hover {
    filter: brightness(1.08) drop-shadow(0 15px 22px rgba(181, 83, 9, 0.6))
      drop-shadow(0 0 18px rgba(255, 159, 38, 0.48));
  }
`;

const GhostBase = styled.a`
  ${hudButtonBase}
  --hud-rim: linear-gradient(110deg, #cbd0d6, #8d959d 20%, #dadee2 50%, #8d959d 80%, #cbd0d6);
  --hud-surface: radial-gradient(120% 140% at 50% -40%, rgba(255, 255, 255, 0.9), transparent 55%),
    linear-gradient(180deg, #ffffff, #edeff1);
  --hud-frame: rgba(120, 128, 137, 0.7);
  --hud-frame-inner: rgba(120, 128, 136, 0.3);
  color: var(--ink);
  ${(p) => p.$size === 'hero' && css`min-width: 368px;`}
  ${(p) => p.$disabled && css`opacity: 0.5;`}

  &:hover {
    color: var(--copper-deep);
    --hud-frame: rgba(201, 117, 26, 0.8);
    --hud-frame-inner: rgba(207, 125, 31, 0.45);
    --hud-accent: #b3641a;
    filter: drop-shadow(0 11px 18px rgba(48, 42, 30, 0.2)) drop-shadow(0 0 11px rgba(201, 117, 26, 0.22));
  }
`;

const NavBase = styled.a`
  ${hudButtonBase}
  --hud-rim: linear-gradient(110deg, #e0902f, #9a5a1f 22%, #d98a2c 50%, #9a5a1f 78%, #e0902f);
  --hud-surface: radial-gradient(110% 120% at 50% 0%, rgba(255, 240, 220, 0.9), transparent 60%),
    linear-gradient(180deg, #fff7ec, #ffe9d2);
  --hud-frame: rgba(201, 117, 26, 0.85);
  --hud-frame-soft: rgba(176, 120, 60, 0.5);
  --hud-frame-inner: rgba(207, 125, 31, 0.4);
  --hud-accent: #c97a1c;
  --hud-side-rails: 1;
  --hud-inset: inset 0 0 22px rgba(150, 90, 25, 0.12);
  color: var(--copper-deep);
  font-weight: 600;
  filter: drop-shadow(0 8px 13px rgba(150, 90, 25, 0.22)) drop-shadow(0 0 8px rgba(201, 117, 26, 0.16));

  &:hover {
    color: #7a3f0e;
    filter: brightness(1.04) drop-shadow(0 10px 16px rgba(150, 90, 25, 0.3))
      drop-shadow(0 0 12px rgba(201, 117, 26, 0.28));
  }
`;

function HudButton({ base: Base, children, ...props }, ref) {
  return (
    <Base ref={ref} {...props}>
      <HudFrame />
      <span className="hud-content">{children}</span>
    </Base>
  );
}

export const ButtonPrimary = React.forwardRef((props, ref) => HudButton({ base: PrimaryBase, ...props }, ref));
export const ButtonGhost = React.forwardRef((props, ref) => HudButton({ base: GhostBase, ...props }, ref));
export const ButtonNav = React.forwardRef((props, ref) => HudButton({ base: NavBase, $size: 'nav', ...props }, ref));

ButtonPrimary.displayName = 'ButtonPrimary';
ButtonGhost.displayName = 'ButtonGhost';
ButtonNav.displayName = 'ButtonNav';

const ArrowBase = styled.button`
  ${hudButtonBase}
  --hud-cut: 11px;
  --hud-body-inset: 7px;
  width: 64px;
  min-width: 64px;
  height: 64px;
  min-height: 64px;
  padding: 0;
  color: ${(p) => (p.$accent ? '#b3641a' : 'var(--ink-dim)')};
  --hud-accent: ${(p) => (p.$accent ? '#d9851f' : 'rgba(120, 128, 138, 0.6)')};
  --hud-frame: ${(p) => (p.$accent ? 'rgba(201, 117, 26, 0.78)' : 'rgba(120, 128, 137, 0.6)')};
  --hud-frame-inner: ${(p) => (p.$accent ? 'rgba(207, 125, 31, 0.35)' : 'rgba(120, 128, 136, 0.28)')};
  --hud-rim: linear-gradient(110deg, #cbd0d6, #9097a0 50%, #cbd0d6);
  --hud-surface: linear-gradient(180deg, #ffffff, #ebedef);

  .hud-content > svg { font-size: 1.55rem; }
  &:hover:not(:disabled) {
    color: var(--copper-deep);
    --hud-frame: rgba(201, 117, 26, 0.85);
    filter: drop-shadow(0 0 12px rgba(201, 117, 26, 0.25));
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

export const HudArrowButton = React.forwardRef(({ children, ...props }, ref) => (
  <ArrowBase ref={ref} {...props}>
    <SquareHudFrame />
    <span className="hud-content">{children}</span>
  </ArrowBase>
));
HudArrowButton.displayName = 'HudArrowButton';

export const hudCompactControl = css`
  --compact-cut: 8px;
  position: relative;
  isolation: isolate;
  border: 0;
  border-radius: 0;
  background: var(--compact-rim, linear-gradient(110deg, #cbd0d6, #9097a0 48%, #cbd0d6));
  clip-path: polygon(
    var(--compact-cut) 0,
    calc(100% - var(--compact-cut)) 0,
    100% var(--compact-cut),
    100% calc(100% - var(--compact-cut)),
    calc(100% - var(--compact-cut)) 100%,
    var(--compact-cut) 100%,
    0 calc(100% - var(--compact-cut)),
    0 var(--compact-cut)
  );

  &::before {
    content: '';
    position: absolute;
    inset: 1.5px;
    z-index: -1;
    background: var(--compact-surface, linear-gradient(180deg, #ffffff, #edeff1));
    clip-path: inherit;
  }
`;

/* --- Status pill with pulsing dot ----------------------------- */
const pulseDot = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(95, 212, 194, 0.55); }
  50% { box-shadow: 0 0 0 6px rgba(95, 212, 194, 0); }
`;

const StatusBase = styled.span`
  ${hudButtonBase}
  --hud-cursor: default;
  --hud-rim: linear-gradient(110deg, #cbd0d6, #9097a0 50%, #cbd0d6);
  --hud-surface: linear-gradient(180deg, #ffffff, #f0f1f2);
  --hud-frame: ${(p) => (p.$color === 'copper' ? 'rgba(201, 117, 26, 0.82)' : 'rgba(120, 128, 137, 0.6)')};
  --hud-frame-soft: rgba(150, 157, 165, 0.7);
  --hud-frame-inner: ${(p) =>
    p.$color === 'copper' ? 'rgba(207, 125, 31, 0.35)' : 'rgba(120, 128, 136, 0.28)'};
  --hud-accent: ${(p) => (p.$color === 'copper' ? '#c97a1c' : '#0f897a')};
  color: var(--ink-dim);
  backdrop-filter: blur(8px);

  &:hover {
    transform: none;
  }
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => (p.$color === 'copper' ? 'var(--copper)' : 'var(--plasma)')};
  animation: ${pulseDot} 2.4s ease-in-out infinite;
  flex: none;
`;

export function StatusPill({ children, color = 'plasma', icon, ...rest }) {
  return (
    <StatusBase $size="status" $color={color} {...rest}>
      <HudFrame />
      <span className="hud-content">
        {icon || <Dot $color={color} aria-hidden="true" />}
        {children}
      </span>
    </StatusBase>
  );
}

/* --- Mono tag, e.g. <DATE> 02—04 JUNE </DATE> ----------------- */
export const MonoTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  color: var(--ink-dim);

  b {
    color: var(--copper);
    font-weight: 500;
  }
`;

/* --- Scroll reveal (IntersectionObserver, CSS-driven) ---------- */
const RevealWrap = styled.div`
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.7s var(--ease-out), transform 0.7s var(--ease-out);
  transition-delay: ${(p) => p.$delay || 0}ms;
  will-change: opacity, transform;

  &.in-view {
    opacity: 1;
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
  }
`;

export function Reveal({ children, delay = 0, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <RevealWrap ref={ref} $delay={delay} {...rest}>
      {children}
    </RevealWrap>
  );
}

/* --- Marquee strip --------------------------------------------- */
const marqueeMove = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const MarqueeOuter = styled.div`
  overflow: hidden;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  padding: 1.1rem 0;
  background: ${(p) => (p.$filled ? 'var(--bg-elev)' : 'transparent')};
  user-select: none;
`;

const MarqueeInner = styled.div`
  display: flex;
  width: max-content;
  animation: ${marqueeMove} ${(p) => p.$duration || 40}s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MarqueeItem = styled.span`
  font-family: var(--font-mono);
  font-size: 0.82rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-faint);
  white-space: nowrap;
  padding: 0 1.6rem;
  display: inline-flex;
  align-items: center;
  gap: 1.6rem;

  &::after {
    content: '✦';
    color: var(--copper);
    font-size: 0.65rem;
  }
`;

export function Marquee({ items, duration, filled }) {
  const doubled = [...items, ...items];
  return (
    <MarqueeOuter $filled={filled} aria-hidden="true">
      <MarqueeInner $duration={duration}>
        {doubled.map((item, i) => (
          <MarqueeItem key={i}>{item}</MarqueeItem>
        ))}
      </MarqueeInner>
    </MarqueeOuter>
  );
}

/* --- Animated counter ------------------------------------------ */
export function useCountUp(target, duration = 1800, started = true) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!started) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(target);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, started]);

  return value;
}

/* --- Atmospheric backdrops ------------------------------------- */
export const DotLattice = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(rgba(34, 31, 26, 0.08) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, black 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, black 30%, transparent 75%);
`;

export const CopperGlow = styled.div`
  position: absolute;
  pointer-events: none;
  width: ${(p) => p.$size || '640px'};
  height: ${(p) => p.$size || '640px'};
  border-radius: 50%;
  background: radial-gradient(circle, var(--copper-glow) 0%, transparent 65%);
  filter: blur(20px);
`;

/* --- Hero wireframe art (copper line motifs, light-theme tuned) --- */
const artSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const artPulse = keyframes`
  0%, 100% { opacity: 0.25; }
  50% { opacity: 0.85; }
`;

export const HeroArt = styled.svg`
  position: absolute;
  right: clamp(1.5rem, 5vw, 4.5rem);
  top: 50%;
  transform: translateY(-50%);
  width: clamp(260px, 33vw, 480px);
  height: auto;
  pointer-events: none;
  user-select: none;
  overflow: visible;
  z-index: 0;
  filter: drop-shadow(0 8px 22px rgba(201, 117, 26, 0.18));

  .frame {
    fill: none;
    stroke: var(--copper);
    stroke-width: 1.1;
    vector-effect: non-scaling-stroke;
  }
  .frame.soft {
    stroke: rgba(201, 117, 26, 0.42);
  }
  .frame.faint {
    stroke: rgba(34, 31, 26, 0.16);
  }
  .frame.dash {
    stroke-dasharray: 4 6;
  }
  .node {
    fill: var(--copper-bright);
  }
  .node.hollow {
    fill: var(--bg);
    stroke: var(--copper);
    stroke-width: 1.4;
    vector-effect: non-scaling-stroke;
  }

  .spin {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${artSpin} 64s linear infinite;
  }
  .spin-rev {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${artSpin} 48s linear infinite reverse;
  }
  .pulse {
    transform-box: fill-box;
    transform-origin: bottom left;
    animation: ${artPulse} 4s ease-in-out infinite;
  }
  .pulse.d1 { animation-delay: 0.6s; }
  .pulse.d2 { animation-delay: 1.2s; }
  .pulse.d3 { animation-delay: 1.8s; }

  @media (prefers-reduced-motion: reduce) {
    .spin, .spin-rev, .pulse { animation: none; }
  }

  @media (max-width: 900px) {
    opacity: 0.45;
    width: clamp(220px, 40vw, 320px);
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

/* --- Page hero (interior pages) --------------------------------- */
const PageHeroWrap = styled.header`
  padding: clamp(8rem, 14vw, 11rem) 0 clamp(3rem, 6vw, 5rem);
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid var(--line);
`;

const PageHeroTitle = styled.h1`
  font-size: clamp(2.6rem, 6.5vw, 4.75rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  max-width: 16ch;
`;

const PageHeroLead = styled.p`
  color: var(--ink-dim);
  font-size: clamp(1rem, 1.5vw, 1.2rem);
  max-width: 58ch;
  margin-top: 1.25rem;
  line-height: 1.7;
`;

export function PageHero({ kicker, index, title, lead, art, children }) {
  return (
    <PageHeroWrap>
      <DotLattice />
      <CopperGlow style={{ top: '-280px', right: '-180px' }} />
      {art}
      <Container style={{ position: 'relative', zIndex: 1 }}>
        <Reveal>
          <Kicker index={index}>{kicker}</Kicker>
          <PageHeroTitle>{title}</PageHeroTitle>
          {lead && <PageHeroLead>{lead}</PageHeroLead>}
          {children}
        </Reveal>
      </Container>
    </PageHeroWrap>
  );
}

/* --- 3D orbital system (pure CSS, gyroscope illusion) ----------- */
const gyro = keyframes`
  from { transform: rotateX(64deg) rotateZ(0deg); }
  to { transform: rotateX(64deg) rotateZ(360deg); }
`;

const ringSpin1 = keyframes`
  from { transform: rotateY(0deg) rotateX(76deg) rotateZ(0deg); }
  to { transform: rotateY(0deg) rotateX(76deg) rotateZ(360deg); }
`;

const ringSpin2 = keyframes`
  from { transform: rotateY(60deg) rotateX(76deg) rotateZ(360deg); }
  to { transform: rotateY(60deg) rotateX(76deg) rotateZ(0deg); }
`;

const ringSpin3 = keyframes`
  from { transform: rotateY(-60deg) rotateX(76deg) rotateZ(0deg); }
  to { transform: rotateY(-60deg) rotateX(76deg) rotateZ(360deg); }
`;

const corePulse = keyframes`
  0%, 100% { box-shadow: 0 0 18px 4px rgba(224, 141, 60, 0.5), 0 0 60px 18px rgba(224, 141, 60, 0.18); }
  50% { box-shadow: 0 0 26px 8px rgba(224, 141, 60, 0.65), 0 0 90px 28px rgba(224, 141, 60, 0.26); }
`;

const OrbitalWrap = styled.div`
  position: absolute;
  width: ${(p) => p.$size || 'min(620px, 72vw)'};
  aspect-ratio: 1;
  pointer-events: none;
  user-select: none;
  perspective: 1100px;
  opacity: ${(p) => p.$opacity || 0.6};

  .system {
    position: absolute;
    inset: 0;
    transform-style: preserve-3d;
    animation: ${gyro} 46s linear infinite;
  }

  .ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid rgba(224, 141, 60, 0.5);
    transform-style: preserve-3d;

    &.r1 {
      animation: ${ringSpin1} 14s linear infinite;
      border-color: rgba(242, 176, 94, 0.55);
    }
    &.r2 {
      inset: 7%;
      animation: ${ringSpin2} 22s linear infinite;
      border-color: rgba(224, 141, 60, 0.4);
    }
    &.r3 {
      inset: 14%;
      animation: ${ringSpin3} 30s linear infinite;
      border-color: rgba(34, 31, 26, 0.18);
    }
  }

  .electron {
    position: absolute;
    top: -3.5px;
    left: 50%;
    width: 7px;
    height: 7px;
    margin-left: -3.5px;
    border-radius: 50%;
    background: var(--copper-bright);
    box-shadow: 0 0 12px 3px rgba(242, 176, 94, 0.7);
  }

  .core {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 14px;
    height: 14px;
    margin: -7px 0 0 -7px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, var(--copper-bright), var(--copper-deep));
    animation: ${corePulse} 3.6s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .system,
    .ring {
      animation: none;
    }
  }
`;

export function Orbital3D({ size, opacity, style }) {
  return (
    <OrbitalWrap $size={size} $opacity={opacity} style={style} aria-hidden="true">
      <div className="system">
        <span className="ring r1">
          <span className="electron" />
        </span>
        <span className="ring r2">
          <span className="electron" />
        </span>
        <span className="ring r3">
          <span className="electron" />
        </span>
      </div>
      <span className="core" />
    </OrbitalWrap>
  );
}

/* --- 3D tilt-on-hover wrapper ----------------------------------- */
const TiltOuter = styled.div`
  perspective: 900px;
  height: 100%;
`;

const TiltInner = styled.div`
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.18s ease-out;
  will-change: transform;
`;

export function Tilt({ children, max = 6, style }) {
  const ref = useRef(null);

  const handleMove = (e) => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const rect = node.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    node.style.transform = `rotateX(${(-py * max).toFixed(2)}deg) rotateY(${(px * max).toFixed(2)}deg) translateZ(6px)`;
  };

  const handleLeave = () => {
    const node = ref.current;
    if (node) node.style.transform = 'rotateX(0deg) rotateY(0deg) translateZ(0)';
  };

  return (
    <TiltOuter style={style} onMouseMove={handleMove} onMouseLeave={handleLeave}>
      <TiltInner ref={ref}>{children}</TiltInner>
    </TiltOuter>
  );
}

/* --- Card ------------------------------------------------------- */
export const Card = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 2rem;
  position: relative;
  transition: transform 0.3s var(--ease-out), border-color 0.3s var(--ease-out),
    background 0.3s var(--ease-out);

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(224, 141, 60, 0.35);
    background: var(--bg-card-hover);
  }
`;
